import { basename, resolve } from "node:path";
import chalk from "chalk";
import ora, { type Ora } from "ora";
import prompts from "prompts";
import type { Config } from "../types";
import { loadConfig, validateConfig } from "../utils/config";
import { componentExists, generateComponents } from "../utils/generator";
import { verbose } from "../utils/logger";
import { getSvgDir } from "../utils/package-root";
import { getAllIcons, iconExists } from "../utils/svg-parser";

type AddOptions = {
  all?: boolean;
  output?: string;
  typescript?: boolean;
  config?: string;
  force?: boolean;
};

function showIconNotFoundError(iconName: string): never {
  console.error(chalk.red(`Error: Icon '${iconName}' not found`));
  console.log(
    chalk.cyan(
      `Run ${chalk.bold("@pyyupsk/pixelarticons list")} to see available icons`,
    ),
  );
  process.exit(1);
}

async function validateIcons(
  iconNames: string[],
  svgDir: string,
): Promise<void> {
  if (iconNames.length === 0) {
    console.error(chalk.red("Error: No icon names provided"));
    console.log(
      chalk.cyan(
        `Run ${chalk.bold("@pyyupsk/pixelarticons list")} to see available icons`,
      ),
    );
    process.exit(1);
  }

  for (const iconName of iconNames) {
    const exists = await iconExists(svgDir, iconName);
    if (!exists) {
      showIconNotFoundError(iconName);
    }
  }
}

async function checkExistingComponents(
  iconNames: string[],
  config: Config,
): Promise<string[]> {
  const iconsToGenerate: string[] = [];
  const existingComponents: string[] = [];

  for (const iconName of iconNames) {
    const exists = await componentExists(iconName, config);
    if (exists) {
      existingComponents.push(iconName);
    } else {
      iconsToGenerate.push(iconName);
    }
  }

  if (existingComponents.length === 0) {
    return iconNames;
  }

  console.log(
    chalk.yellow(
      `\n⚠ ${existingComponents.length} component(s) already exist:`,
    ),
  );
  for (const name of existingComponents.slice(0, 5)) {
    console.log(chalk.dim(`  - ${name}`));
  }
  if (existingComponents.length > 5) {
    console.log(chalk.dim(`  ... and ${existingComponents.length - 5} more`));
  }

  const response = await prompts({
    type: "confirm",
    name: "overwrite",
    message: "Overwrite existing components?",
    initial: false,
  });

  if (response.overwrite === undefined) {
    console.log(chalk.yellow("\nOperation cancelled"));
    process.exit(0);
  }

  if (response.overwrite) {
    return iconNames;
  }

  if (iconsToGenerate.length === 0) {
    console.log(chalk.yellow("\nNo new components to generate"));
    process.exit(0);
  }

  console.log(
    chalk.cyan(`\nGenerating ${iconsToGenerate.length} new component(s)...`),
  );
  return iconsToGenerate;
}

async function resolveIconsToGenerate(
  options: AddOptions,
  iconNames: string[],
  svgDir: string,
  config: Config,
): Promise<string[]> {
  if (options.all) {
    const spinner = ora("Finding all icons...").start();
    const icons = await getAllIcons(svgDir);
    spinner.succeed(`Found ${icons.length} icons`);

    if (!options.force) {
      return await checkExistingComponents(icons, config);
    }
    return icons;
  }

  await validateIcons(iconNames, svgDir);

  if (!options.force) {
    return await checkExistingComponents(iconNames, config);
  }
  return iconNames;
}

function applyConfigOverrides(config: Config, options: AddOptions): void {
  if (options.output) {
    config.output = options.output;
    verbose(`Output overridden to: ${options.output}`);
  }
  if (options.typescript !== undefined) {
    config.typescript = options.typescript;
    config.extension = options.typescript ? "tsx" : "jsx";
    verbose(`TypeScript mode: ${config.typescript}`);
  }
}

function displaySummary(
  generatedPaths: string[],
  iconsToGenerate: string[],
  config: Config,
): void {
  console.log(chalk.cyan(`\nOutput directory: ${resolve(config.output)}`));

  if (iconsToGenerate.length <= 5) {
    console.log(chalk.dim("\nGenerated components:"));
    for (const p of generatedPaths) {
      console.log(chalk.dim(`  - ${basename(p)}`));
    }
  }

  console.log(chalk.green("\n✓ Components generated successfully!"));

  if (config.indexFile) {
    console.log(chalk.cyan("\nUsage:"));
    console.log(chalk.white(`  import { Archive } from '${config.output}';`));
  }
}

function createProgressCallback(spinner: Ora) {
  return (current: number, total: number, iconName: string) => {
    spinner.text = `Generating components: ${current}/${total} (${iconName})`;
  };
}

export async function addCommand(
  iconNames: string[],
  options: AddOptions,
): Promise<void> {
  try {
    const config = loadConfig(options.config);
    verbose("Loaded configuration:", JSON.stringify(config, null, 2));

    applyConfigOverrides(config, options);

    validateConfig(config);
    verbose("Configuration validated successfully");

    const svgDir = getSvgDir(import.meta.url);

    const iconsToGenerate = await resolveIconsToGenerate(
      options,
      iconNames,
      svgDir,
      config,
    );

    const spinner = ora("Generating components...").start();
    const generatedPaths = await generateComponents(
      iconsToGenerate,
      svgDir,
      config,
      createProgressCallback(spinner),
    );

    spinner.succeed(chalk.green(`Generated ${generatedPaths.length} files`));
    displaySummary(generatedPaths, iconsToGenerate, config);
  } catch (error) {
    console.error(
      chalk.red("Error generating components:"),
      (error as Error).message,
    );
    process.exit(1);
  }
}
